import SelectOrganisation from 'components/SelectOrganisation/SelectOrganisation';
import OrganisationIcon from 'components/Svgs/icons/Organisation';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { GroupOrganisationHashMap } from 'utils/createGroupOrganisationMap';
import {
  TextV2,
  RadioGroupControl,
  SectionTitle,
} from '@insights-ltd/design-library/components';
import { OrgsInGroupSearch } from 'components/OrgsInGroupSearch';
import { useTranslation } from 'react-i18next';
import { FieldErrors } from 'react-hook-form';
import { OrganisationResponse } from 'api';
import { Box, styled } from '@mui/material';
import useIsPermitted from 'components/hooks/useIsPermitted';
import { TeamFormData } from './types';
import { TeamVariant } from './CreateTeam.helper';

export const StyledIcon = (icon: any, color: string) =>
  styled(icon)({
    fill: color,
    height: '20px',
  });

type OrgOptionProps = {
  setTeamType: (value: TeamVariant) => void;
  teamType: TeamVariant;
  reset: () => void;
  hasMultiOrgAccess?: boolean;
};

export const OrgsOptionsSelector = ({
  setTeamType,
  teamType,
  reset,
  hasMultiOrgAccess = false,
}: OrgOptionProps) => {
  const { t } = useTranslation();
  const { isPermitted } = useIsPermitted([
    { action: 'Multi_Organisation_Team_Create', scope: 'Global' },
  ]);
  const teamTypeOptions = [
    {
      label: t('ui.event-management.learners.teams.create.single-org-option'),
      value: 'single-org',
    },
    {
      label: t('ui.event-management.learners.teams.create.multi-org-option'),
      value: 'multi-org',
    },
  ];
  if (isPermitted && hasMultiOrgAccess) {
    return (
      <>
        <TextV2 component="p" variant="bodyBold" sx={{ marginBottom: '1rem' }}>
          {t('ui.event-management.learners.teams.create.team-type-label')}
        </TextV2>
        <RadioGroupControl
          name="multi-or-single-org-selector"
          title="selector"
          variant="boxed"
          options={teamTypeOptions}
          onChange={(option) => {
            setTeamType(option as TeamVariant);
            reset();
          }}
          value={teamType}
        />
      </>
    );
  }
  return null;
};

export type OrgSelectorProp = {
  isMultiOrg: boolean;
  setSelectedOrgs: (org: any) => void;
  selectedOrgs: OrganisationResponse[];
  removeOrgs: (org: any) => void;
  organisations: OrganisationResponse[];
  showOrganisationDropdown: boolean;
  handleOrganisationChange: (value: any) => void;
  errors: FieldErrors<TeamFormData>;
  organisationsInGroups?: GroupOrganisationHashMap;
  selectedOrg?: OrganisationResponse | null;
};
export const OrgSelector = ({
  isMultiOrg,
  setSelectedOrgs,
  selectedOrgs,
  removeOrgs,
  organisations,
  showOrganisationDropdown,
  handleOrganisationChange,
  errors,
  organisationsInGroups,
  selectedOrg,
}: OrgSelectorProp) => {
  const { t } = useTranslation();
  const title = isMultiOrg
    ? t('ui.event-management.organisations.title')
    : t('ui.event-management.users.create.organisation', { count: 1 });
  const associatedGroup = selectedOrg
    ? organisationsInGroups?.[selectedOrg?.id]
    : undefined;
  return (
    <div>
      {!!organisations?.length && showOrganisationDropdown && (
        <div>
          <Box
            sx={{
              py: spacingSizeMap.L,
            }}
          >
            <SectionTitle
              icon={{ icon: <OrganisationIcon />, iconColour: 'orange' }}
              text={title}
            />
          </Box>
          {isMultiOrg ? (
            <>
              <TextV2 variant="bodyBold">{title}</TextV2>
              <OrgsInGroupSearch
                onSelect={setSelectedOrgs}
                dataTestId="multi-org-selector"
                loading={false}
                organisationsInGroups={organisationsInGroups}
                labelKey="ui.event-management.organisations.select-organisations"
                selectedMultiOrgs={selectedOrgs}
                selectedGroup={null}
                t={t}
                onRemoveOrg={removeOrgs}
                showOrgList
              />
            </>
          ) : (
            <SelectOrganisation
              onSelect={(organisation) => {
                handleOrganisationChange(organisation.id);
              }}
              onChange={() => {
                handleOrganisationChange('');
              }}
              associatedGroup={associatedGroup}
              selectedOrg={selectedOrg ?? undefined}
              isError={Boolean(errors?.organisationId)}
              errorText={t(
                'ui.event-management.events.create.error.value-required',
              )}
            />
          )}
        </div>
      )}
    </div>
  );
};
